'use client'

import { Box, Divider, ListSubheader, Menu, MenuItem, TextField, Tooltip, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import type { OrgNode } from './types'

type OrgChildrenMenuProps = {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  currentOrg: OrgNode
  childOrgs: OrgNode[]
  /** True while the org-tree customers query has not settled. */
  childrenLoading?: boolean
  /** Ancestors listed above children on mobile. */
  ancestors?: OrgNode[]
  onSelect: (orgId: string) => void
}

const SEARCH_THRESHOLD = 8

export default function OrgChildrenMenu({
  anchorEl,
  open,
  onClose,
  currentOrg,
  childOrgs,
  childrenLoading = false,
  ancestors = [],
  onSelect
}: OrgChildrenMenuProps) {
  const t = useTranslations('orgBar')
  const [query, setQuery] = useState('')

  const filteredChildren = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return childOrgs
    return childOrgs.filter((child) => child.name.toLowerCase().includes(q))
  }, [childOrgs, query])

  const showSearch = childOrgs.length > SEARCH_THRESHOLD
  const emptyMessage = childrenLoading
    ? t('loadingSubOrgs')
    : childOrgs.length > 0 && query.trim()
      ? t('noMatchingSubOrgs')
      : t('noSubOrgs')

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={() => {
        setQuery('')
        onClose()
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: {
            mt: '4px',
            minWidth: 280,
            width: 320,
            maxWidth: 360,
            maxHeight: 360
          }
        }
      }}
      MenuListProps={{
        dense: true,
        subheader: (
          <ListSubheader
            disableSticky
            sx={{
              py: 1.5,
              px: 3,
              lineHeight: 1.3,
              bgcolor: 'background.paper',
              // Override MUI ListSubheader’s default overline-ish treatment.
              textTransform: 'none',
              letterSpacing: 'normal',
              fontSize: 'inherit',
              fontWeight: 'inherit'
            }}
          >
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.25 }}>
              {t('subOrgsLabel')}
            </Typography>
            <Tooltip title={currentOrg.name}>
              <Typography variant="body2" fontWeight={600} color="text.primary" noWrap component="div">
                {currentOrg.name}
              </Typography>
            </Tooltip>
          </ListSubheader>
        )
      }}
    >
      {ancestors.length > 0 && (
        <Box>
          {ancestors.map((ancestor) => (
            <MenuItem
              key={`ancestor-${ancestor.id}`}
              onClick={() => {
                onSelect(ancestor.id)
                setQuery('')
                onClose()
              }}
            >
              <Typography variant="body2" noWrap>
                {ancestor.name}
              </Typography>
            </MenuItem>
          ))}
          <Divider sx={{ my: 1 }} />
        </Box>
      )}

      {showSearch && (
        <Box px={2} pb={1} onKeyDown={(e) => e.stopPropagation()}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('searchSubOrgs')}
            inputProps={{ 'aria-label': t('searchSubOrgs') }}
          />
        </Box>
      )}

      {filteredChildren.length === 0 ? (
        <MenuItem disabled>
          {/* text.secondary on night paper is too dim; use primary at reduced
              opacity so empty/loading copy stays readable on the dark menu. */}
          <Typography variant="body2" sx={{ color: 'text.primary', opacity: 0.72 }}>
            {emptyMessage}
          </Typography>
        </MenuItem>
      ) : (
        filteredChildren.map((child) => (
          <MenuItem
            key={child.id}
            onClick={() => {
              onSelect(child.id)
              setQuery('')
              onClose()
            }}
          >
            <Typography variant="body2" noWrap>
              {child.name}
            </Typography>
          </MenuItem>
        ))
      )}
    </Menu>
  )
}
